import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API_BASE } from '../apiBase';
import type { Locale } from '../studioLocale';
import type { PresetFacets } from './WorkspaceStarterModal';
import { StudioIcon } from './StudioIcons';

export type ReferenceMode = 'disabled' | 'auto' | 'manual' | 'mixed';
export type NoveltyLevel = 'low' | 'medium' | 'high';

interface ReferenceSummary {
  id: string;
  title: string;
  title_zh?: string;
  genres?: string[];
  source_based_summary?: string;
  themes?: string[];
  plot_devices?: string[];
}

interface ReferencePreviewReference {
  id: string;
  title: string;
  title_zh?: string;
  genres?: string[];
  summary?: string;
}

interface ReferencePreview {
  available?: boolean;
  selected_references?: ReferencePreviewReference[];
  bundle?: Record<string, any>;
}

interface ReferenceStrategyPanelProps {
  locale: Locale;
  worldview: string;
  themes: string[];
  prompt: string;
  presetFacets?: PresetFacets | null;
  selectedReferenceIds: string[];
  onReferenceChange: (ids: string[]) => void;
  referenceMode: ReferenceMode;
  onReferenceModeChange: (mode: ReferenceMode) => void;
  noveltyLevel: NoveltyLevel;
  onNoveltyLevelChange: (level: NoveltyLevel) => void;
}

const COPY = {
  zh: {
    title: '参考策略',
    subtitle: '只借用结构、节奏、冲突机制和装置，不复刻原作角色姓名、台词和具体桥段。',
    expand: '展开',
    collapse: '收起',
    modeTitle: '参考模式',
    noveltyTitle: '原创力度',
    libraryTitle: '参考库',
    previewTitle: '本次生成将参考',
    previewEmpty: '当前未启用参考库。生成会完全按你的结构和提示词执行。',
    previewLoading: '正在预览参考组合...',
    previewError: '参考预览失败',
    selected: (count: number) => `已选 ${count} 条`,
    autoPick: (count: number) => `预览命中 ${count} 条`,
    disableNote: '关闭后，结构生成和剧本生成都不会自动混入参考作品。',
    manualHint: '手动最多选择 4 条，适合明确借鉴某类戏剧引擎。',
    mixedHint: '先给系统几个种子参考，再让系统补齐相近结构模式。',
    autoHint: '系统会根据世界观、主题和提示词自动检索相近参考。',
    chooseHint: '选择真实作品作为结构参考，但不会直接复制角色名、对白或桥段细节。',
    sourceThemes: '主题',
    sourceDevices: '装置',
    hookAtoms: '钩子原子',
    revealAtoms: '揭示节奏',
    endingAtoms: '回收方式',
    emptyLibrary: '参考库暂时为空。',
    disabled: '关闭',
    auto: '自动检索',
    manual: '手动选择',
    mixed: '混合模式',
    low: '保守',
    medium: '均衡',
    high: '高原创',
    selectedPill: '已选择',
  },
  en: {
    title: 'Reference Strategy',
    subtitle: 'Borrow structure, pacing, conflict engines, and devices only. Do not copy names, lines, or scene-specific execution.',
    expand: 'Expand',
    collapse: 'Collapse',
    modeTitle: 'Reference mode',
    noveltyTitle: 'Originality',
    libraryTitle: 'Reference library',
    previewTitle: 'This run will draw from',
    previewEmpty: 'Reference mode is off. Generation will follow only your structure and prompt.',
    previewLoading: 'Previewing reference bundle...',
    previewError: 'Failed to preview reference bundle',
    selected: (count: number) => `${count} selected`,
    autoPick: (count: number) => `${count} matched in preview`,
    disableNote: 'When disabled, neither structure generation nor script generation will inject reference works.',
    manualHint: 'Pick up to 4 references manually when you want a very explicit dramatic engine.',
    mixedHint: 'Choose seed references first, then let the system blend adjacent patterns.',
    autoHint: 'The system will retrieve nearby references from worldview, themes, and prompt.',
    chooseHint: 'Use real works as structural references without reproducing exact names, dialogue, or scene execution.',
    sourceThemes: 'Themes',
    sourceDevices: 'Devices',
    hookAtoms: 'Hook atoms',
    revealAtoms: 'Reveal rhythm',
    endingAtoms: 'Payoff modes',
    emptyLibrary: 'The reference library is currently empty.',
    disabled: 'Off',
    auto: 'Auto',
    manual: 'Manual',
    mixed: 'Mixed',
    low: 'Low',
    medium: 'Medium',
    high: 'High',
    selectedPill: 'Selected',
  },
} as const;

const MODE_META = {
  zh: [
    { value: 'disabled' as ReferenceMode, label: '关闭', note: '完全不使用参考库' },
    { value: 'auto' as ReferenceMode, label: '自动检索', note: '系统自动检索相近作品' },
    { value: 'manual' as ReferenceMode, label: '手动选择', note: '只使用你勾选的作品' },
    { value: 'mixed' as ReferenceMode, label: '混合模式', note: '你提供种子，系统补齐相近模式' },
  ],
  en: [
    { value: 'disabled' as ReferenceMode, label: 'Off', note: 'Do not use the reference library' },
    { value: 'auto' as ReferenceMode, label: 'Auto', note: 'Retrieve similar works automatically' },
    { value: 'manual' as ReferenceMode, label: 'Manual', note: 'Use only the works you selected' },
    { value: 'mixed' as ReferenceMode, label: 'Mixed', note: 'Use your picks as seeds, then blend adjacent patterns' },
  ],
} as const;

const NOVELTY_META = {
  zh: [
    { value: 'low' as NoveltyLevel, label: '保守', note: '更贴近类型语法，但仍避免直接复刻' },
    { value: 'medium' as NoveltyLevel, label: '均衡', note: '混合多个模式，并保持可辨识的类型感' },
    { value: 'high' as NoveltyLevel, label: '高原创', note: '更强调重组、反转和跨来源合成' },
  ],
  en: [
    { value: 'low' as NoveltyLevel, label: 'Low', note: 'Stay close to genre grammar while avoiding direct copying' },
    { value: 'medium' as NoveltyLevel, label: 'Medium', note: 'Blend multiple engines with a recognizable genre shape' },
    { value: 'high' as NoveltyLevel, label: 'High', note: 'Favor recombination, inversion, and cross-source synthesis' },
  ],
} as const;

function extractAtoms(bundle: Record<string, any> | undefined, key: string) {
  const items = Array.isArray(bundle?.[key]) ? bundle?.[key] : [];
  return items
    .map((item: any) => String(item?.atom || '').trim())
    .filter(Boolean)
    .slice(0, 4);
}

function joinList(items: string[], locale: Locale) {
  return items.join(locale === 'zh' ? '、' : ' · ');
}

const ReferenceStrategyPanel: React.FC<ReferenceStrategyPanelProps> = ({
  locale,
  worldview,
  themes,
  prompt,
  presetFacets,
  selectedReferenceIds,
  onReferenceChange,
  referenceMode,
  onReferenceModeChange,
  noveltyLevel,
  onNoveltyLevelChange,
}) => {
  const text = COPY[locale];
  const modeMeta = MODE_META[locale] as ReadonlyArray<{ value: ReferenceMode; label: string; note: string }>;
  const noveltyMeta = NOVELTY_META[locale] as ReadonlyArray<{ value: NoveltyLevel; label: string; note: string }>;
  const [expanded, setExpanded] = useState(false);
  const [references, setReferences] = useState<ReferenceSummary[]>([]);
  const [preview, setPreview] = useState<ReferencePreview>({ selected_references: [], bundle: {} });
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState('');

  useEffect(() => {
    let cancelled = false;

    const loadReferences = async () => {
      try {
        const response = await axios.get(`${API_BASE}/api/reference-library`);
        if (!cancelled) {
          setReferences(response.data?.references || []);
        }
      } catch (_error) {
        if (!cancelled) {
          setReferences([]);
        }
      }
    };

    loadReferences();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadPreview = async () => {
      if (!expanded || referenceMode === 'disabled') {
        setPreview({ selected_references: [], bundle: {} });
        setPreviewError('');
        setLoadingPreview(false);
        return;
      }

      try {
        setLoadingPreview(true);
        setPreviewError('');
        const response = await axios.post(`${API_BASE}/api/reference-pattern-bundle`, {
          worldview,
          themes,
          prompt,
          genre_tags: presetFacets?.search_tags || [],
          target_formula: presetFacets ? Object.values(presetFacets.choice_labels || {}).filter(Boolean) : [],
          preset_facets: presetFacets || undefined,
          reference_ids: selectedReferenceIds,
          reference_mode: referenceMode,
          novelty_level: noveltyLevel,
        });
        if (!cancelled) {
          setPreview({
            available: response.data?.available,
            selected_references: response.data?.selected_references || [],
            bundle: response.data?.bundle || {},
          });
        }
      } catch (error: any) {
        if (!cancelled) {
          setPreview({ selected_references: [], bundle: {} });
          setPreviewError(error?.response?.data?.error || error?.message || text.previewError);
        }
      } finally {
        if (!cancelled) {
          setLoadingPreview(false);
        }
      }
    };

    loadPreview();
    return () => {
      cancelled = true;
    };
  }, [expanded, worldview, themes, prompt, presetFacets, selectedReferenceIds, referenceMode, noveltyLevel, text.previewError]);

  const previewReferences = useMemo(
    () => (Array.isArray(preview.selected_references) ? preview.selected_references : []),
    [preview.selected_references],
  );

  const hookAtoms = extractAtoms(preview.bundle, 'hook_bundle');
  const revealAtoms = extractAtoms(preview.bundle, 'reveal_bundle');
  const endingAtoms = extractAtoms(preview.bundle, 'ending_bundle');
  const currentModeMeta = modeMeta.find((item) => item.value === referenceMode) || modeMeta[0];
  const currentNoveltyMeta = noveltyMeta.find((item) => item.value === noveltyLevel) || noveltyMeta[0];

  const currentModeNote =
    referenceMode === 'disabled'
      ? text.disableNote
      : referenceMode === 'manual'
      ? text.manualHint
      : referenceMode === 'mixed'
      ? text.mixedHint
      : text.autoHint;

  const toggleReference = (referenceId: string) => {
    const exists = selectedReferenceIds.includes(referenceId);
    if (exists) {
      onReferenceChange(selectedReferenceIds.filter((item) => item !== referenceId));
      return;
    }
    const next = [...selectedReferenceIds, referenceId].slice(0, 4);
    if (referenceMode === 'disabled') {
      onReferenceModeChange('manual');
    }
    onReferenceChange(next);
  };

  return (
    <section
      style={{
        display: 'grid',
        gap: '12px',
        marginTop: '12px',
        borderRadius: '18px',
        border: '1px solid rgba(133, 148, 166, 0.16)',
        background: 'rgba(248, 250, 250, 0.9)',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: '16px',
          padding: '14px 16px',
          background: 'linear-gradient(180deg, rgba(250, 251, 252, 0.96), rgba(244, 247, 248, 0.96))',
        }}
      >
        <div style={{ display: 'grid', gap: '6px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '999px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(15, 138, 106, 0.1)',
                color: '#0f8a6a',
              }}
            >
              <StudioIcon name="library" />
            </span>
            <strong style={{ fontSize: '14px', color: '#111827' }}>{text.title}</strong>
          </div>
          <p style={{ margin: 0, fontSize: '12px', lineHeight: 1.65, color: '#5b6472' }}>{text.subtitle}</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                minHeight: '28px',
                padding: '0 10px',
                borderRadius: '999px',
                background: 'rgba(255,255,255,0.9)',
                border: '1px solid rgba(133, 148, 166, 0.16)',
                fontSize: '11px',
                fontWeight: 700,
                color: '#475467',
              }}
            >
              {currentModeMeta.label}
            </span>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                minHeight: '28px',
                padding: '0 10px',
                borderRadius: '999px',
                background: referenceMode === 'disabled' ? 'rgba(255,255,255,0.9)' : 'rgba(240, 251, 247, 0.92)',
                border:
                  referenceMode === 'disabled'
                    ? '1px solid rgba(133, 148, 166, 0.16)'
                    : '1px solid rgba(16, 163, 127, 0.14)',
                fontSize: '11px',
                fontWeight: 700,
                color: referenceMode === 'disabled' ? '#475467' : '#0f8a6a',
              }}
            >
              {text.noveltyTitle}: {currentNoveltyMeta.label}
            </span>
            {(referenceMode === 'manual' || referenceMode === 'mixed') && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  minHeight: '28px',
                  padding: '0 10px',
                  borderRadius: '999px',
                  background: 'rgba(255,255,255,0.9)',
                  border: '1px solid rgba(133, 148, 166, 0.16)',
                  fontSize: '11px',
                  fontWeight: 700,
                  color: '#475467',
                }}
              >
                {text.selected(selectedReferenceIds.length)}
              </span>
            )}
            {expanded && referenceMode !== 'disabled' && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  minHeight: '28px',
                  padding: '0 10px',
                  borderRadius: '999px',
                  background: 'rgba(240, 251, 247, 0.92)',
                  border: '1px solid rgba(16, 163, 127, 0.14)',
                  fontSize: '11px',
                  fontWeight: 700,
                  color: '#0f8a6a',
                }}
              >
                {text.autoPick(previewReferences.length)}
              </span>
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={() => setExpanded((value) => !value)}
          style={{
            minHeight: '38px',
            padding: '0 12px',
            borderRadius: '12px',
            border: '1px solid rgba(133, 148, 166, 0.16)',
            background: 'rgba(255,255,255,0.92)',
            cursor: 'pointer',
            color: '#111827',
            fontWeight: 600,
          }}
        >
          {expanded ? text.collapse : text.expand}
        </button>
      </div>

      {expanded && (
        <div style={{ display: 'grid', gap: '14px', padding: '0 16px 16px' }}>
          <div style={{ display: 'grid', gap: '8px' }}>
            <div style={{ fontSize: '12px', color: '#5b6472', fontWeight: 700 }}>{text.modeTitle}</div>
            <div style={{ position: 'relative' }}>
              <select
                value={referenceMode}
                onChange={(event) => onReferenceModeChange(event.target.value as ReferenceMode)}
                style={{
                  width: '100%',
                  minHeight: '40px',
                  padding: '0 12px',
                  borderRadius: '12px',
                  border: '1px solid rgba(133, 148, 166, 0.16)',
                  background: 'rgba(255,255,255,0.92)',
                  color: '#111827',
                  cursor: 'pointer',
                  fontWeight: 700,
                }}
              >
                {modeMeta.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ fontSize: '12px', color: '#667085', lineHeight: 1.6 }}>{currentModeNote}</div>
          </div>

          <div style={{ display: 'grid', gap: '8px' }}>
            <div style={{ fontSize: '12px', color: '#5b6472', fontWeight: 700 }}>{text.noveltyTitle}</div>
            <div style={{ position: 'relative' }}>
              <select
                value={noveltyLevel}
                onChange={(event) => onNoveltyLevelChange(event.target.value as NoveltyLevel)}
                disabled={referenceMode === 'disabled'}
                style={{
                  width: '100%',
                  minHeight: '40px',
                  padding: '0 12px',
                  borderRadius: '12px',
                  border: '1px solid rgba(133, 148, 166, 0.16)',
                  background: 'rgba(255,255,255,0.92)',
                  color: '#111827',
                  cursor: referenceMode === 'disabled' ? 'not-allowed' : 'pointer',
                  opacity: referenceMode === 'disabled' ? 0.56 : 1,
                  fontWeight: 700,
                }}
              >
                {noveltyMeta.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ fontSize: '12px', color: '#667085', lineHeight: 1.6 }}>{currentNoveltyMeta.note}</div>
          </div>

          <div
            style={{
              display: 'grid',
              gap: '10px',
              padding: '12px 14px',
              borderRadius: '16px',
              background: 'rgba(255,255,255,0.82)',
              border: '1px solid rgba(133, 148, 166, 0.12)',
            }}
          >
            <div style={{ fontSize: '12px', color: '#5b6472', fontWeight: 700 }}>{text.previewTitle}</div>

            {referenceMode === 'disabled' && <div style={{ fontSize: '13px', color: '#667085' }}>{text.previewEmpty}</div>}

            {referenceMode !== 'disabled' && loadingPreview && (
              <div style={{ fontSize: '13px', color: '#667085' }}>{text.previewLoading}</div>
            )}

            {referenceMode !== 'disabled' && !loadingPreview && previewError && (
              <div style={{ fontSize: '13px', color: '#b42318' }}>
                {text.previewError}: {previewError}
              </div>
            )}

            {referenceMode !== 'disabled' && !loadingPreview && !previewError && (
              <>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {previewReferences.map((reference) => (
                    <span
                      key={reference.id}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        minHeight: '30px',
                        padding: '0 10px',
                        borderRadius: '999px',
                        background: 'rgba(240, 251, 247, 0.92)',
                        border: '1px solid rgba(16, 163, 127, 0.14)',
                        color: '#0f8a6a',
                        fontSize: '12px',
                        fontWeight: 700,
                      }}
                    >
                      {reference.title_zh ? `${reference.title_zh} / ${reference.title}` : reference.title}
                    </span>
                  ))}
                </div>

                <div style={{ display: 'grid', gap: '8px' }}>
                  {hookAtoms.length > 0 && (
                    <div style={{ fontSize: '12px', color: '#475467', lineHeight: 1.65 }}>
                      <strong>{text.hookAtoms}:</strong> {joinList(hookAtoms, locale)}
                    </div>
                  )}
                  {revealAtoms.length > 0 && (
                    <div style={{ fontSize: '12px', color: '#475467', lineHeight: 1.65 }}>
                      <strong>{text.revealAtoms}:</strong> {joinList(revealAtoms, locale)}
                    </div>
                  )}
                  {endingAtoms.length > 0 && (
                    <div style={{ fontSize: '12px', color: '#475467', lineHeight: 1.65 }}>
                      <strong>{text.endingAtoms}:</strong> {joinList(endingAtoms, locale)}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          <div style={{ display: 'grid', gap: '8px' }}>
            <div style={{ fontSize: '12px', color: '#5b6472', fontWeight: 700 }}>{text.libraryTitle}</div>
            <div style={{ fontSize: '12px', color: '#667085', lineHeight: 1.6 }}>{text.chooseHint}</div>

            <div style={{ display: 'grid', gap: '10px', maxHeight: '320px', overflow: 'auto', paddingRight: '2px' }}>
              {references.length === 0 && <div style={{ fontSize: '13px', color: '#667085' }}>{text.emptyLibrary}</div>}

              {references.map((reference) => {
                const selected = selectedReferenceIds.includes(reference.id);
                return (
                  <button
                    key={reference.id}
                    type="button"
                    onClick={() => toggleReference(reference.id)}
                    style={{
                      textAlign: 'left',
                      display: 'grid',
                      gap: '8px',
                      padding: '12px 14px',
                      borderRadius: '16px',
                      border: selected
                        ? '1px solid rgba(16, 163, 127, 0.24)'
                        : '1px solid rgba(133, 148, 166, 0.14)',
                      background: selected ? 'rgba(240, 251, 247, 0.9)' : 'rgba(255,255,255,0.9)',
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'flex-start' }}>
                      <div style={{ display: 'grid', gap: '4px' }}>
                        <strong style={{ fontSize: '13px', color: '#111827' }}>
                          {reference.title_zh ? `${reference.title_zh} / ${reference.title}` : reference.title}
                        </strong>
                        <div style={{ fontSize: '11px', color: '#667085' }}>
                          {joinList((reference.genres || []).slice(0, 5), locale)}
                        </div>
                      </div>
                      {selected && (
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            minHeight: '26px',
                            padding: '0 9px',
                            borderRadius: '999px',
                            background: '#0f8a6a',
                            color: '#ffffff',
                            fontSize: '11px',
                            fontWeight: 700,
                          }}
                        >
                          {text.selectedPill}
                        </span>
                      )}
                    </div>

                    <div style={{ fontSize: '12px', color: '#475467', lineHeight: 1.65 }}>
                      {reference.source_based_summary || ''}
                    </div>

                    {Array.isArray(reference.themes) && reference.themes.length > 0 && (
                      <div style={{ fontSize: '11px', color: '#667085' }}>
                        <strong>{text.sourceThemes}:</strong> {joinList(reference.themes.slice(0, 4), locale)}
                      </div>
                    )}

                    {Array.isArray(reference.plot_devices) && reference.plot_devices.length > 0 && (
                      <div style={{ fontSize: '11px', color: '#667085' }}>
                        <strong>{text.sourceDevices}:</strong> {joinList(reference.plot_devices.slice(0, 4), locale)}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default ReferenceStrategyPanel;
